import React, { useState } from 'react';
import {
    Box,
    Typography,
    Grid,
    Paper,
    Divider,
    List,
    ListItem,
    ListItemIcon,
    ListItemText,
    Chip,
    Card,
    CardContent,
    Link,
    Button,
} from '@mui/material';

import {
    PeopleAlt,
    PersonAdd,
    Home,
    ThumbUpAlt,
    ThumbDownAlt,
    Lightbulb,
    CheckCircle,
    ErrorOutline,
    MenuBook,
    Chat,
    Star,
    Close,
    KeyboardArrowRight,
} from '@mui/icons-material';

// スクロール用の関数
const scrollToSection = (sectionId) => {
    const element = document.getElementById(sectionId);
    if (element) {
        const offset = 80; // ヘッダーの高さ分のオフセット
        const elementPosition = element.getBoundingClientRect().top;
        const offsetPosition = elementPosition + window.pageYOffset - offset;

        window.scrollTo({
            top: offsetPosition,
            behavior: 'smooth'
        });
    }
};

const tocItems = [
    { id: 'introduction', label: 'はじめに：ルームシェアの新しい選択肢' },
    { id: 'friends', label: '「友達」とルームシェアする特徴・メリット・デメリット' },
    { id: 'strangers', label: '「知らない人」とルームシェアする特徴・メリット・デメリット' },
    { id: 'comparison', label: '友達 vs. 知らない人 〜タイプ別徹底比較〜' },
    { id: 'tips', label: '失敗しないルームシェアのポイント' },
    { id: 'stories', label: '体験談：リアルな声から学ぶ' },
    { id: 'summary', label: 'まとめ：自分に合ったルームシェアを選ぼう' },
];

const PostContent = () => {
    const [showToc, setShowToc] = useState(true);
    return (
        <Box sx={{ maxWidth: 800, mx: 'auto', px: 3 }}>
            {showToc ? (
                <Card sx={{ mb: 4, boxShadow: 2, bgcolor: '#e3f2fd', border: '1px solid #ddd', borderRadius: 2 }}>
                    <CardContent sx={{ p: 2 }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                            <Typography variant="h6" sx={{ flexGrow: 1 }}>目次</Typography>
                            <Button
                                variant="outlined"
                                size="small"
                                onClick={() => setShowToc(false)}
                                startIcon={<Close fontSize="small" />}
                            >
                                閉じる
                            </Button>
                        </Box>
                        <List sx={{ p: 0 }}>
                            {tocItems.map((item, idx) => (
                                <ListItem key={item.id} sx={{ p: 0.5, display: 'flex', alignItems: 'center' }}>
                                    <Box sx={{
                                        minWidth: 32,
                                        minHeight: 32,
                                        bgcolor: '#f5f5f5',
                                        color: '#888',
                                        fontWeight: 'bold',
                                        borderRadius: 1.5,
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        mr: 1,
                                        fontSize: '1.1rem'
                                    }}>
                                        {idx + 1}.
                                    </Box>
                                    <Link
                                        component="button"
                                        onClick={() => scrollToSection(item.id)}
                                        color="inherit"
                                        underline="hover"
                                        sx={{ cursor: 'pointer', fontSize: '0.97rem', textAlign: 'left' }}
                                    >
                                        {item.label}
                                    </Link>
                                </ListItem>
                            ))}
                        </List>
                    </CardContent>
                </Card>
            ) : (
                <Button
                    variant="outlined"
                    size="small"
                    onClick={() => setShowToc(true)}
                    sx={{ mb: 2 }}
                    startIcon={<MenuBook />}
                >
                    目次を表示
                </Button>
            )}

            {/* 導入 */}
            <Box id="introduction" sx={{ mb: 4 }}>
                <Typography variant="h4" component="h2" gutterBottom>
                    はじめに：ルームシェアの新しい選択肢
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                    住まいのコストを抑えつつ、日常に彩りや刺激を与えてくれる「ルームシェア」。近年、20〜30代を中心に多様な価値観やライフスタイルをもつ人々の間で、その人気は高まり続けています。
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                    しかし、いざルームシェアを始めようと考えたとき、<b>「友達と住むべきか？」「まったく知らない人と住むべきか？」</b>という大きな選択肢に直面することが少なくありません。
                </Typography>
                <Box sx={{ my: 2 }}>
                    <img
                        src="/images/blog/living-dining-share_roomshare-friends-vs-strangers-merit-demerit.jpg"
                        alt="明るい共有リビングで多国籍のルームメイトたちがダイニングテーブルを囲み、互いに笑顔で会話しているシーン。カラフルなインテリアと広々とした空間が、ルームシェアの明るく開放的な雰囲気を伝えている。"
                        width="100%"
                        style={{ borderRadius: 8, boxShadow: '0 2px 10px rgba(0,0,0,0.08)', marginBottom: 12 }}
                    />
                    <Typography variant="caption" color="text.secondary" sx={{ pl: 1 }}>
                        さまざまな価値観が交差するルームシェアの暮らし
                    </Typography>
                </Box>
                <Typography variant="body1">
                    この記事では、両者の「メリット・デメリット」「実際の体験談」「失敗しないコツ」まで、ルームシェアを検討するあなたに役立つリアルな情報をお届けします。
                </Typography>
            </Box>

            {/* 友達とルームシェア */}
            <Box id="friends" sx={{ mb: 4 }}>
                <Typography variant="h4" component="h2" gutterBottom>
                    <PeopleAlt sx={{ verticalAlign: 'middle', mr: 1, color: 'primary.main' }} />
                    「友達」とルームシェアする特徴・メリット・デメリット
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                    気心の知れた友人とルームシェアをするのは、安心感や楽しさが大きな魅力です。しかし、その一方で「仲が良いからこそ」生じる課題も存在します。ここでは特徴を詳しく見ていきましょう。
                </Typography>
                <Grid container spacing={2}>
                    <Grid item xs={12} md={6}>
                        <Paper elevation={2} sx={{ p: 2, mb: 2, borderRadius: 2 }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                                <ThumbUpAlt color="primary" sx={{ mr: 1 }} />
                                <Typography variant="h6">メリット</Typography>
                            </Box>
                            <List>
                                <ListItem>
                                    <ListItemIcon><CheckCircle color="success" /></ListItemIcon>
                                    <ListItemText primary="気軽に本音を話せる安心感" />
                                </ListItem>
                                <ListItem>
                                    <ListItemIcon><CheckCircle color="success" /></ListItemIcon>
                                    <ListItemText primary="金銭管理やルール決めが柔軟" />
                                </ListItem>
                                <ListItem>
                                    <ListItemIcon><CheckCircle color="success" /></ListItemIcon>
                                    <ListItemText primary="趣味や生活リズムが合いやすい" />
                                </ListItem>
                                <ListItem>
                                    <ListItemIcon><CheckCircle color="success" /></ListItemIcon>
                                    <ListItemText primary="孤独感が少なく、日常が楽しい" />
                                </ListItem>
                            </List>
                        </Paper>
                    </Grid>
                    <Grid item xs={12} md={6}>
                        <Paper elevation={2} sx={{ p: 2, mb: 2, borderRadius: 2 }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                                <ThumbDownAlt color="error" sx={{ mr: 1 }} />
                                <Typography variant="h6">デメリット</Typography>
                            </Box>
                            <List>
                                <ListItem>
                                    <ListItemIcon><ErrorOutline color="warning" /></ListItemIcon>
                                    <ListItemText primary="お金や家事分担などで揉めやすい" />
                                </ListItem>
                                <ListItem>
                                    <ListItemIcon><ErrorOutline color="warning" /></ListItemIcon>
                                    <ListItemText primary="プライベートの境界が曖昧になる" />
                                </ListItem>
                                <ListItem>
                                    <ListItemIcon><ErrorOutline color="warning" /></ListItemIcon>
                                    <ListItemText primary="トラブルが起きた時に関係修復が難しい" />
                                </ListItem>
                                <ListItem>
                                    <ListItemIcon><ErrorOutline color="warning" /></ListItemIcon>
                                    <ListItemText primary="「友達」が「同居人」に変わる寂しさ" />
                                </ListItem>
                            </List>
                        </Paper>
                    </Grid>
                </Grid>
                <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                    <Lightbulb sx={{ verticalAlign: 'middle', mr: 0.5, color: 'warning.main' }} />
                    人間関係が近い分、些細なルール違反や生活習慣の違いがストレスに感じやすい傾向があります。
                </Typography>
            </Box>

            {/* 知らない人とルームシェア */}
            <Box id="strangers" sx={{ mb: 4 }}>
                <Typography variant="h4" component="h2" gutterBottom>
                    <PersonAdd sx={{ verticalAlign: 'middle', mr: 1, color: 'primary.main' }} />
                    「知らない人」とルームシェアする特徴・メリット・デメリット
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                    入居時はまったくの他人同士でも、ルームシェアを通じて新しい人間関係が生まれるのがこのスタイルの醍醐味。どんな特徴があり、どんな注意点があるのでしょうか？
                </Typography>
                <Grid container spacing={2}>
                    <Grid item xs={12} md={6}>
                        <Paper elevation={2} sx={{ p: 2, mb: 2, borderRadius: 2 }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                                <ThumbUpAlt color="primary" sx={{ mr: 1 }} />
                                <Typography variant="h6">メリット</Typography>
                            </Box>
                            <List>
                                <ListItem>
                                    <ListItemIcon><CheckCircle color="success" /></ListItemIcon>
                                    <ListItemText primary="自分のペースやプライバシーを保ちやすい" />
                                </ListItem>
                                <ListItem>
                                    <ListItemIcon><CheckCircle color="success" /></ListItemIcon>
                                    <ListItemText primary="新しい価値観や人脈に出会える" />
                                </ListItem>
                                <ListItem>
                                    <ListItemIcon><CheckCircle color="success" /></ListItemIcon>
                                    <ListItemText primary="契約やルールが明確でトラブルが少ない" />
                                </ListItem>
                                <ListItem>
                                    <ListItemIcon><CheckCircle color="success" /></ListItemIcon>
                                    <ListItemText primary="適度な距離感を保てる" />
                                </ListItem>
                            </List>
                        </Paper>
                    </Grid>
                    <Grid item xs={12} md={6}>
                        <Paper elevation={2} sx={{ p: 2, mb: 2, borderRadius: 2 }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                                <ThumbDownAlt color="error" sx={{ mr: 1 }} />
                                <Typography variant="h6">デメリット</Typography>
                            </Box>
                            <List>
                                <ListItem>
                                    <ListItemIcon><ErrorOutline color="warning" /></ListItemIcon>
                                    <ListItemText primary="コミュニケーションに気を遣う" />
                                </ListItem>
                                <ListItem>
                                    <ListItemIcon><ErrorOutline color="warning" /></ListItemIcon>
                                    <ListItemText primary="生活リズムや価値観の違いに戸惑う" />
                                </ListItem>
                                <ListItem>
                                    <ListItemIcon><ErrorOutline color="warning" /></ListItemIcon>
                                    <ListItemText primary="信頼関係を築くまでに時間がかかる" />
                                </ListItem>
                                <ListItem>
                                    <ListItemIcon><ErrorOutline color="warning" /></ListItemIcon>
                                    <ListItemText primary="最初は緊張や不安を感じやすい" />
                                </ListItem>
                            </List>
                        </Paper>
                    </Grid>
                </Grid>
                <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                    <Lightbulb sx={{ verticalAlign: 'middle', mr: 0.5, color: 'warning.main' }} />
                    「ビジネスライクな関係」から始まり、徐々に距離が縮まるパターンが多いのが特徴です。
                </Typography>
                <Box sx={{ my: 2 }}>
                    <img
                        src="/images/blog/first-meeting-share_roomshare-friends-vs-strangers-merit-demerit.jpg"
                        alt="シンプルなダイニングテーブルを囲み、初対面のルームメイト同士が少し緊張しながらも丁寧に自己紹介している場面。白と木目を基調とした明るい室内が、クリーンで安心感のある雰囲気を演出している。"
                        width="100%"
                        style={{ borderRadius: 8, boxShadow: '0 2px 10px rgba(0,0,0,0.08)', marginBottom: 12 }}
                    />
                    <Typography variant="caption" color="text.secondary" sx={{ pl: 1 }}>
                        初対面同士で始めるルームシェアは、適度な距離感と新しい出会いが魅力
                    </Typography>
                </Box>
            </Box>

            {/* 比較 */}
            <Box id="comparison" sx={{ mb: 4 }}>
                <Typography variant="h4" component="h2" gutterBottom>
                    <Home sx={{ verticalAlign: 'middle', mr: 1, color: 'primary.main' }} />
                    友達 vs. 知らない人 〜タイプ別徹底比較〜
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                    それぞれの特徴を表でまとめると、次のようになります。あなたに合うのはどちら？
                </Typography>
                <Box sx={{ overflowX: 'auto', mb: 2 }}>
                    <table style={{ width: '100%', borderCollapse: 'collapse', marginBottom: 10 }}>
                        <thead>
                            <tr>
                                <th style={{ border: '1px solid #ddd', background: '#f5f5f5', padding: 8, fontWeight: 'bold' }}></th>
                                <th style={{ border: '1px solid #ddd', background: '#e3f2fd', padding: 8, fontWeight: 'bold', textAlign: 'center' }}>
                                    <PeopleAlt sx={{ verticalAlign: 'middle', mr: 1, color: '#1976d2' }} fontSize="small" />
                                    友達とシェア
                                </th>
                                <th style={{ border: '1px solid #ddd', background: '#fffde7', padding: 8, fontWeight: 'bold', textAlign: 'center' }}>
                                    <PersonAdd sx={{ verticalAlign: 'middle', mr: 1, color: '#ffb300' }} fontSize="small" />
                                    知らない人とシェア
                                </th>
                            </tr>
                        </thead>
                        <tbody>
                            <tr>
                                <td style={{ border: '1px solid #ddd', padding: 8, fontWeight: 'bold' }}>安心感</td>
                                <td style={{ border: '1px solid #ddd', padding: 8 }}>◎</td>
                                <td style={{ border: '1px solid #ddd', padding: 8 }}>△</td>
                            </tr>
                            <tr>
                                <td style={{ border: '1px solid #ddd', padding: 8, fontWeight: 'bold' }}>コスト管理</td>
                                <td style={{ border: '1px solid #ddd', padding: 8 }}>柔軟（ただし曖昧になりやすい）</td>
                                <td style={{ border: '1px solid #ddd', padding: 8 }}>明確（契約やルール重視）</td>
                            </tr>
                            <tr>
                                <td style={{ border: '1px solid #ddd', padding: 8, fontWeight: 'bold' }}>トラブル時の対応</td>
                                <td style={{ border: '1px solid #ddd', padding: 8 }}>感情的になりやすい</td>
                                <td style={{ border: '1px solid #ddd', padding: 8 }}>割り切りやすい</td>
                            </tr>
                            <tr>
                                <td style={{ border: '1px solid #ddd', padding: 8, fontWeight: 'bold' }}>プライバシー</td>
                                <td style={{ border: '1px solid #ddd', padding: 8 }}>やや曖昧</td>
                                <td style={{ border: '1px solid #ddd', padding: 8 }}>確保しやすい</td>
                            </tr>
                            <tr>
                                <td style={{ border: '1px solid #ddd', padding: 8, fontWeight: 'bold' }}>出会い・刺激</td>
                                <td style={{ border: '1px solid #ddd', padding: 8 }}>少なめ</td>
                                <td style={{ border: '1px solid #ddd', padding: 8 }}>多い</td>
                            </tr>
                        </tbody>
                    </table>
                </Box>
                <Typography variant="body2" color="text.secondary">
                    <Star sx={{ verticalAlign: 'middle', mr: 0.5, color: 'secondary.main' }} />
                    「安心感重視」なら友達、「新しい出会い・成長」なら知らない人、という選び方がおすすめです。
                </Typography>
            </Box>

            {/* 失敗しないポイント */}
            <Box id="tips" sx={{ mb: 4 }}>
                <Typography variant="h4" component="h2" gutterBottom>
                    <Lightbulb sx={{ verticalAlign: 'middle', mr: 1, color: 'primary.main' }} />
                    失敗しないルームシェアのポイント
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                    どちらのタイプにせよ、ルームシェアで失敗しないためには事前準備とコミュニケーションが不可欠です。以下に、専門的な観点から重要なポイントをまとめました。
                </Typography>
                <Grid container spacing={2}>
                    <Grid item xs={12} sm={6}>
                        <Paper elevation={1} sx={{ p: 2, borderRadius: 2, mb: 2 }}>
                            <Typography variant="subtitle1" sx={{ mb: 1, fontWeight: 'bold' }}>
                                <CheckCircle color="primary" sx={{ mr: 1 }} />
                                必ず守りたいチェックポイント
                            </Typography>
                            <List>
                                <ListItem>
                                    <ListItemIcon><KeyboardArrowRight /></ListItemIcon>
                                    <ListItemText
                                        primary="入居前に「家事・掃除・ゴミ出し」などの役割分担を明確にする"
                                    />
                                </ListItem>
                                <ListItem>
                                    <ListItemIcon><KeyboardArrowRight /></ListItemIcon>
                                    <ListItemText
                                        primary="家賃・光熱費の精算ルールを必ず書面化"
                                    />
                                </ListItem>
                                <ListItem>
                                    <ListItemIcon><KeyboardArrowRight /></ListItemIcon>
                                    <ListItemText
                                        primary="お金の貸し借りは基本的に避ける"
                                    />
                                </ListItem>
                                <ListItem>
                                    <ListItemIcon><KeyboardArrowRight /></ListItemIcon>
                                    <ListItemText
                                        primary="お互いのプライバシーや生活リズムを尊重する"
                                    />
                                </ListItem>
                            </List>
                        </Paper>
                    </Grid>
                    <Grid item xs={12} sm={6}>
                        <Paper elevation={1} sx={{ p: 2, borderRadius: 2, mb: 2 }}>
                            <Typography variant="subtitle1" sx={{ mb: 1, fontWeight: 'bold' }}>
                                <Lightbulb color="warning" sx={{ mr: 1 }} />
                                専門的アドバイス
                            </Typography>
                            <List>
                                <ListItem>
                                    <ListItemIcon><Chat color="primary" /></ListItemIcon>
                                    <ListItemText primary="定期的なミーティングで不満や希望を出し合う" />
                                </ListItem>
                                <ListItem>
                                    <ListItemIcon><Chat color="primary" /></ListItemIcon>
                                    <ListItemText primary="LINEやSlackなど連絡ツールを活用" />
                                </ListItem>
                                <ListItem>
                                    <ListItemIcon><Chat color="primary" /></ListItemIcon>
                                    <ListItemText primary="契約書やルールブックをファイルで共有" />
                                </ListItem>
                                <ListItem>
                                    <ListItemIcon><Chat color="primary" /></ListItemIcon>
                                    <ListItemText primary="「無理に仲良くしすぎない」距離感を意識" />
                                </ListItem>
                            </List>
                        </Paper>
                    </Grid>
                </Grid>
                <Box sx={{ my: 2 }}>

                    <Typography variant="caption" color="text.secondary" sx={{ pl: 1 }}>
                        ルールや役割分担を可視化することで、トラブルを未然に防ぐことができる
                    </Typography>
                </Box>
                <Typography variant="body2" color="text.secondary">
                    <Star sx={{ verticalAlign: 'middle', mr: 0.5, color: 'secondary.main' }} />
                    「最初はちょっと面倒でも、きちんとルールを決めること」が、長く快適なルームシェアの秘訣です。
                </Typography>
            </Box>

            {/* 体験談 */}
            <Box id="stories" sx={{ mb: 4 }}>
                <Typography variant="h4" component="h2" gutterBottom>
                    <MenuBook sx={{ verticalAlign: 'middle', mr: 1, color: 'primary.main' }} />
                    体験談：リアルな声から学ぶ
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                    ここでは実際に「友達」と「知らない人」両方のパターンでルームシェアを経験した人たちのリアルな声を紹介します。
                </Typography>
                <Grid container spacing={2}>
                    <Grid item xs={12} sm={6}>
                        <Card sx={{ maxWidth: 345, mb: 2, mx: 'auto', bgcolor: '#f5f5f5' }}>
                            <CardContent>
                                <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                                    <PeopleAlt sx={{ color: 'primary.main', mr: 1 }} />
                                    <Typography variant="subtitle1" sx={{ fontWeight: 'bold' }}>
                                        友達とのシェア体験談
                                    </Typography>
                                </Box>
                                <Typography variant="body2" sx={{ mb: 1 }}>
                                    <i>「最初は本当に楽しかったです。毎日笑いが絶えなかったし、悩みもすぐ相談できる。でも、家事やゴミ出しのルールが曖昧で喧嘩が増えて…気付けば友達というより『同居人』に。最後は少し距離を置くことになりましたが、今は良い思い出です。」</i>
                                </Typography>
                                <Typography variant="caption" color="text.secondary">（26歳・女性・会社員）</Typography>
                            </CardContent>
                        </Card>
                    </Grid>
                    <Grid item xs={12} sm={6}>
                        <Card sx={{ maxWidth: 345, mb: 2, mx: 'auto', bgcolor: '#fffde7' }}>
                            <CardContent>
                                <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                                    <PersonAdd sx={{ color: 'warning.main', mr: 1 }} />
                                    <Typography variant="subtitle1" sx={{ fontWeight: 'bold' }}>
                                        知らない人とのシェア体験談
                                    </Typography>
                                </Box>
                                <Typography variant="body2" sx={{ mb: 1 }}>
                                    <i>「最初は緊張しましたが、ルールがしっかり決まっていたので安心でした。プライベートも守られて、思ったより快適。ルームシェア相手とは今でも良い関係です！」</i>
                                </Typography>
                                <Typography variant="caption" color="text.secondary">（29歳・男性・ITエンジニア）</Typography>
                            </CardContent>
                        </Card>
                    </Grid>
                </Grid>
                <Divider sx={{ my: 2 }} />
                <Typography variant="body2" color="text.secondary">
                    <Chat sx={{ verticalAlign: 'middle', mr: 0.5, color: 'primary.main' }} />
                    体験者の多くが「ルール・距離感・事前相談の重要性」を実感しています。
                </Typography>
            </Box>

            {/* まとめ */}
            <Box id="summary" sx={{ mb: 4 }}>
                <Typography variant="h4" component="h2" gutterBottom>
                    <Star sx={{ verticalAlign: 'middle', mr: 1, color: 'secondary.main' }} />
                    まとめ：自分に合ったルームシェアを選ぼう
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                    ルームシェアは、コストだけでなく人間関係や日常の充実度にも大きな影響を与える選択です。「友達」との安心感を重視するか、「知らない人」との新鮮な出会いを求めるか——どちらにも明確なメリット・デメリットがあります。
                </Typography>
                <Box sx={{
                    display: 'flex',
                    flexWrap: 'wrap',
                    gap: 1,
                    mb: 2
                }}>
                    <Chip label="安心感" color="primary" variant="outlined" />
                    <Chip label="刺激・成長" color="secondary" variant="outlined" />
                    <Chip label="プライバシー" color="success" variant="outlined" />
                    <Chip label="ルール作り" color="default" variant="outlined" />
                </Box>
                <Typography variant="body1" sx={{ mb: 1 }}>
                    最後に、「事前の話し合い」と「お互いを尊重する姿勢」が、どんなルームシェアにも欠かせません。
                </Typography>
                <Typography variant="body2" color="text.secondary">
                    ぜひ本記事を参考に、ご自分にぴったりのシェアスタイルを見つけてください！
                </Typography>
            </Box>
        </Box>
    );
};

const post = {
    id: 'roomshare-friends-vs-strangers-merit-demerit',
    slug: 'roomshare-friends-vs-strangers-merit-demerit',
    title: 'ルームシェアで「友達」と住む？「知らない人」と住む？メリット・デメリット比較',
    excerpt: '友人とのルームシェアと、初対面同士のシェアの特徴やメリット・デメリットを徹底比較。失敗しないポイントや体験談も交え、あなたに最適な選択肢を解説します。',
    coverImage: '/images/blog/cover-roomshare-friends-vs-strangers_roomshare-friends-vs-strangers-merit-demerit.jpg',
    coverImageAlt: '明るい自然光が差し込むモダンなルームシェアの共有リビングで、多様な背景を持つルームメイトたちが楽しく会話をしている様子。温かみのある木目調の家具や観葉植物が配置され、アットホームかつスタイリッシュな雰囲気が全体を包んでいる。',
    publishedAt: new Date().toISOString(),
    isPublished: true,
    categories: ['ルームシェア', '住まい選び', '暮らしのコツ'],
    tags: ['ルームシェア', '友達', '初対面', '体験談', 'トラブル防止'],
    author: {
        name: '小林 輝',
        avatar: '/images/avatars/teru-kobayashi.jpg',
        bio: '国内外のルームシェア事情に精通し、実体験と数多くの取材をもとに「安心・快適なシェア生活」のノウハウを発信。自身も10年以上のシェア経験を持つ。',
        profilePicture: '/images/avatars/teru-kobayashi.jpg'
    },
    viewCount: 1280,
    contentComponent: PostContent
};

export default post;